import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { Activity, useState, useMemo, useEffect, useRef } from "react";
import { cn, humanizeDuration, generateTextShadow } from "@/lib/utils";
import { ArenaBar } from "@/components/ArenaBar";
import { Countdown } from "@/components/ui/countdown";
import type { Player } from "@/lib/types/api";
import type { ChampionshipDetailsResponse } from "@/lib/types/api";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { PrivateChampionshipOpen } from "../-components/PrivateChampionshipOpen";
import ArenaChartECharts from "@/components/ArenaChartECharts";
import { t } from "@/lib/i18n";
import { NavigationPrivateChampionshipLive } from "../-components/NavigationPrivateChampionshipLive";
import { PrivateChampionshipEnded } from "../-components/PrivateChampionshipEnded";
import { useHostedChampionshipDetail } from "@/lib/hooks/useApi";
import configs from "@/lib/configs";
import AnimatedLoading from "@/components/AnimatedLoading";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { useMusic } from "@/lib/hooks/useMusic";
import { MATCH_TRACK, MENU_TRACK } from "@/lib/configs/music";
import { getUserIconElement, getDomainTypeFromUsername } from "@/lib/username";
import dayjs from "dayjs";
import { getSyncedNow, updateClockOffset } from "@/lib/clockSync";

type PrivateChampionshipMatchSearch = {
  fromChampionship?: boolean;
};

type TournamentPlayerJoinedEvent = {
  type: "tournament_player_joined";
  tournament_id: string;
  joined_count: number;
  player: Player | null;
};

type TournamentStatusChangedEvent = {
  type: "tournament_status_changed";
  tournament_id: string;
  old_status: string;
  new_status: string;
};

type TournamentScheduleChangedEvent = {
  type: "tournament_schedule_changed";
  tournament_id: string;
  start_date_epoch: number | null;
  end_date_epoch: number | null;
};

function PrivateChampionshipMatchDetails() {
  const { tournamentId, matchId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [liveSquadValues, setLiveSquadValues] = useState<Map<string, number>>(
    new Map(),
  );
  const [data, setData] = useState<ChampionshipDetailsResponse | null>(null);
  const [isSseError, setIsSseError] = useState(false);
  const hasSnapshotRef = useRef(false);
  const { play } = useMusic();

  const {
    data: initialTournament,
    isPending: isTournamentQueryPending,
    error,
    refetch: refetchTournamentDetails,
  } = useHostedChampionshipDetail(matchId, { refetchInterval: false });

  const tournament = data ?? initialTournament;

  // SSE: real-time updates for this championship match
  useEffect(() => {
    if (!matchId) return;
    if (initialTournament?.status === "completed") return;
    let isActive = true;
    const eventSource = new EventSource(
      `${configs.API_BASE_URL}/sse/tournaments/${tournamentId}?kind=hosted_championship&matchId=${matchId}`,
    );

    const handleSnapshot = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(messageEvent.data) as
          | (ChampionshipDetailsResponse & { serverTime?: number })
          | null;
        if (payload && typeof payload.serverTime === "number") {
          updateClockOffset(payload.serverTime);
        }
        setData(payload ?? null);
        setIsSseError(false);
        hasSnapshotRef.current = true;
      } catch (err) {
        console.error("Failed to parse championship match snapshot:", err);
        setIsSseError(true);
      }
    };

    const handlePlayerJoined = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as TournamentPlayerJoinedEvent;
        setData((prev) => {
          if (!prev || prev.tournamentId !== payload.tournament_id) return prev;
          let nextPlayers = prev.players;
          if (payload.player) {
            const exists = prev.players.some(
              (p) => p.squadAddress === payload.player?.squadAddress,
            );
            if (!exists) nextPlayers = [...prev.players, payload.player];
          }
          return {
            ...prev,
            joinedPlayers: nextPlayers.length,
            players: nextPlayers,
          };
        });
      } catch (err) {
        console.error("Failed to parse player joined:", err);
      }
    };

    const handleStatusChanged = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as TournamentStatusChangedEvent;
        setData((prev) => {
          if (!prev || prev.tournamentId !== payload.tournament_id) return prev;
          return { ...prev, status: payload.new_status };
        });
        if (payload.new_status === "completed") {
          refetchTournamentDetails().then((result) => {
            if (isActive && result.data) setData(result.data);
          });
        }
      } catch (err) {
        console.error("Failed to parse status changed:", err);
      }
    };

    const handleScheduleChanged = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as TournamentScheduleChangedEvent;
        setData((prev) => {
          if (!prev || prev.tournamentId !== payload.tournament_id) return prev;
          return {
            ...prev,
            startDate: payload.start_date_epoch ?? prev.startDate,
            endDate: payload.end_date_epoch ?? prev.endDate,
          };
        });
      } catch (err) {
        console.error("Failed to parse schedule changed:", err);
      }
    };

    const handleStreamError = () => {
      if (!isActive) return;
      setIsSseError(true);
    };

    const handleMessage = (event: MessageEvent<string>) => {
      if (!isActive) return;
      try {
        const payload = JSON.parse(event.data) as Record<string, unknown> & {
          type?: string;
        };
        if (!payload || typeof payload !== "object") return;
        if ("tournamentId" in payload) {
          handleSnapshot(event as Event);
          return;
        }
        if (payload.type === "tournament_player_joined")
          handlePlayerJoined(event as Event);
        else if (payload.type === "tournament_status_changed")
          handleStatusChanged(event as Event);
        else if (payload.type === "tournament_schedule_changed")
          handleScheduleChanged(event as Event);
      } catch (err) {
        console.error("Failed to parse message:", err);
      }
    };

    eventSource.addEventListener("snapshot", handleSnapshot);
    eventSource.addEventListener(
      "tournament_player_joined",
      handlePlayerJoined,
    );
    eventSource.addEventListener(
      "tournament_status_changed",
      handleStatusChanged,
    );
    eventSource.addEventListener(
      "tournament_schedule_changed",
      handleScheduleChanged,
    );
    eventSource.addEventListener("error", handleStreamError);
    eventSource.addEventListener("message", handleMessage);
    eventSource.onerror = handleStreamError;

    return () => {
      isActive = false;
      eventSource.close();
    };
  }, [matchId, initialTournament?.status, refetchTournamentDetails]);

  useEffect(() => {
    if (hasSnapshotRef.current || !initialTournament) return;
    setData((prev) => prev ?? initialTournament);
  }, [initialTournament]);

  const isLive = useMemo(() => tournament?.status === "live", [tournament]);
  const isEnded = useMemo(
    () => tournament?.status === "completed",
    [tournament],
  );
  const isWaiting = useMemo(
    () => tournament?.status === "waiting",
    [tournament],
  );
  const isCancelled = useMemo(
    () => tournament?.status === "cancelled",
    [tournament],
  );

  const isNewTournament = useMemo(() => {
    if (!tournament) return true;
    return tournament.joinedPlayers > 10;
  }, [tournament]);

  const tournamentNameParts = useMemo(() => {
    if (!tournament) return null;
    const name = tournament.tournamentName;
    const lowerName = name.toLowerCase();
    const hasDomain =
      lowerName.includes("skr") ||
      lowerName.includes(".world.id") ||
      lowerName.includes("cfldotfun.sol") ||
      lowerName.includes(".sol");
    if (!hasDomain) {
      return null;
    }
    const parts = name.split(/ vs /i);
    const player1 = parts[0]?.trim() || "";
    const player2 = parts[1]?.trim() || "";
    return {
      player1,
      player2,
      player1Icon: getUserIconElement(getDomainTypeFromUsername(player1), "w-4 h-4"),
      player2Icon: getUserIconElement(getDomainTypeFromUsername(player2), "w-4 h-4"),
    };
  }, [tournament?.tournamentName]);

  useEffect(() => {
    if (!tournament) return;
    let targetTimestamp: number | null = null;
    if (tournament.status === "waiting") {
      targetTimestamp = Number(tournament.startDate);
    } else if (tournament.status === "live") {
      targetTimestamp = Number(tournament.endDate);
    } else {
      return;
    }

    const checkTimeAndRefetch = () => {
      const now = dayjs(getSyncedNow());
      const target = dayjs.unix(targetTimestamp!);
      const diff = target.diff(now, "second");
      if (diff <= 0) refetchTournamentDetails();
    };

    checkTimeAndRefetch();
    const interval = setInterval(checkTimeAndRefetch, 3000);
    return () => clearInterval(interval);
  }, [tournament, refetchTournamentDetails]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isLive) {
      play({ ...MATCH_TRACK, autoPlay: true });

      return () => {
        play({ ...MENU_TRACK, autoPlay: true });
      };
    }
  }, [isLive, play]);

  const navigateBack = () => {
    if (search?.fromChampionship) {
      router.history.back();
    } else {
      navigate({
        to: "/private-championship/$tournamentId",
        params: { tournamentId },
      });
    }
  };

  const handlePlayerClick = (player: Player) => {
    setSelectedPlayer(player);
  };

  const handleSquadValuesUpdate = (squadValues: Map<string, number>) => {
    setLiveSquadValues(squadValues);
  };

  if (!mounted) {
    return <AnimatedLoading />;
  }

  const isLoading = isTournamentQueryPending && !tournament;
  if (isLoading) {
    return <AnimatedLoading />;
  }

  if (error || !tournament) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <FloatingLeftButton
          onClick={navigateBack}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
        <p className="text-white/50">Match not found.</p>
      </div>
    );
  }

  const sseErrorOverlay = (
    <Activity mode={isSseError ? "visible" : "hidden"}>
      <div className="fixed inset-0 z-1002! flex items-center justify-center bg-indigo-950/90 backdrop-blur-xs px-6 text-left">
        <div className="relative w-88 sm:w-[24rem] flex flex-col items-center gap-6 text-center overflow-x-visible">
          <div className="relative flex flex-col items-center justify-center w-full h-40 overflow-x-visible">
            <img
              src="/assets/connection-error.png"
              alt="Connection Error"
              className="h-40 object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>
          <div className="flex flex-col items-center">
            <div className="text-2xl font-bold z-100 uppercase">
              <AnimatedWaiting
                prefix="Reconnecting"
                className="text-white!"
                style={{ textShadow: generateTextShadow(1.5) }}
              />
            </div>
          </div>
        </div>
      </div>
    </Activity>
  );

  return (
    <>
      {sseErrorOverlay}
      <div
        className={cn(
          "flex-1 flex flex-col",
          !isEnded && "bg-gray-50",
          isEnded && "overflow-hidden relative",
        )}
      >
        <div className="flex flex-col flex-1 min-h-0">
          {/* Back Button */}
          {!isWaiting && (
            <FloatingLeftButton
              onClick={navigateBack}
              className="absolute left-0 top-4 z-50"
              icon={
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 9 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                    fill="white"
                  />
                </svg>
              }
            />
          )}

          {(isWaiting || isCancelled) && (
            <PrivateChampionshipOpen
              tournamentId={tournamentId}
              matchId={matchId}
            />
          )}

          {isLive && (
            <>
              <div
                className="h-full max-h-[60svh] flex-1 flex flex-col gap-3 overflow-hidden"
                style={{
                  backgroundImage:
                    "url(https://cdn.navalabs.io/assets/bg-live-tournament_JeXUeg.png)",
                  backgroundSize: "cover",
                  backgroundPosition: "top",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <div className="flex flex-col items-center pt-2 gap-1">
                  <span className="text-white font-black text-2xl tracking-wider text-center">
                    {!tournamentNameParts ||
                    (!tournamentNameParts.player1Icon &&
                      !tournamentNameParts.player2Icon) ? (
                      tournament.tournamentName
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap justify-center">
                        <span className="flex items-center gap-1">
                          <span>{tournamentNameParts.player1}</span>
                          {tournamentNameParts.player1Icon && (
                            <span className="shrink-0">
                              {tournamentNameParts.player1Icon}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0">vs</span>
                        <span className="flex items-center gap-1">
                          <span>{tournamentNameParts.player2}</span>
                          {tournamentNameParts.player2Icon && (
                            <span className="shrink-0">
                              {tournamentNameParts.player2Icon}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </span>

                  <Countdown
                    className="text-white font-black text-lg tracking-wider text-center"
                    startTimestamp={Number(tournament.endDate)}
                  />

                  <div className="flex flex-col gap-1 w-full max-w-xs uppercase text-shadow-black">
                    <div className="flex justify-between items-center">
                      <span className="text-yellow-400 font-bold text-base">
                        {t("championship.playersJoined")}
                      </span>
                      <span className="text-white font-bold text-base">
                        {tournament.joinedPlayers}/{tournament.maximumPlayer}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-yellow-400 font-bold text-base">
                        {t("championship.duration")}
                      </span>
                      <span className="text-white font-bold text-base">
                        {humanizeDuration(tournament.duration)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="px-1 flex flex-1 mb-2 overflow-hidden">
                  {isNewTournament ? (
                    <ArenaBar
                      players={tournament.players}
                      tournament={tournament}
                      onPlayerClick={handlePlayerClick}
                      onSquadValuesUpdate={handleSquadValuesUpdate}
                    />
                  ) : (
                    <ArenaChartECharts
                      players={tournament.players}
                      onSquadValuesUpdate={handleSquadValuesUpdate}
                      matchStartedAt={
                        tournament.endDate
                          ? (Number(tournament.endDate) - tournament.duration) *
                            1000
                          : undefined
                      }
                    />
                  )}
                </div>
              </div>
            </>
          )}

          {isLive && (
            <NavigationPrivateChampionshipLive
              tournamentDetails={tournament}
              tournamentId={tournamentId}
              className="h-full max-h-[40svh]"
              onSquadSelect={handlePlayerClick}
              selectedPlayer={selectedPlayer}
              liveSquadValues={liveSquadValues}
            />
          )}

          {isEnded && (
            <PrivateChampionshipEnded
              tournament={tournament}
              tournamentId={tournamentId}
              roundNumber={tournament.roundNumber}
              roundName={tournament.roundName}
            />
          )}
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute(
  "/(game)/private-championship/$tournamentId/$matchId",
)({
  component: PrivateChampionshipMatchDetails,
  validateSearch: (
    search: Record<string, unknown>,
  ): PrivateChampionshipMatchSearch => {
    return {
      fromChampionship:
        search.fromChampionship === "true" || search.fromChampionship === true,
    };
  },
});
